// Copyright (c) Ping. All rights reserved.

import React, { useCallback, useMemo } from 'react';
import { Pressable, StyleSheet, Text, type TextStyle, View, type ViewStyle } from 'react-native';
import { Avatar } from '../components/Avatar';
import { createTheme } from '../design-system/theme';
import { useColorScheme } from '../foundations/colorScheme';
import { callHistoryStrings } from './strings';
import type { CallDirection, CallHistoryEntry, CallKind } from './types';

export interface CallHistoryRowProps {
  readonly entry: CallHistoryEntry;
  readonly selected?: boolean;
  readonly onPress?: ((entry: CallHistoryEntry) => void) | undefined;
  readonly onLongPress?: ((entry: CallHistoryEntry) => void) | undefined;
  readonly onToggleFavorite?: ((entry: CallHistoryEntry) => void) | undefined;
}

/** Single call-history row, mirroring iOS-E `CallHistoryRow` + Android `PingCallHistoryRow`. */
export const CallHistoryRow = React.memo<CallHistoryRowProps>(
  ({ entry, selected = false, onPress, onLongPress, onToggleFavorite }) => {
    const scheme = useColorScheme();
    const theme = createTheme(scheme);
    const { summary } = entry;

    const handlePress = useCallback(() => {
      onPress?.(entry);
    }, [entry, onPress]);

    const handleLongPress = useCallback(() => {
      onLongPress?.(entry);
    }, [entry, onLongPress]);

    const handleToggleFavorite = useCallback(() => {
      onToggleFavorite?.(entry);
    }, [entry, onToggleFavorite]);

    const formattedTime = useMemo(() => formatRelativeTime(summary.startedAt), [summary.startedAt]);

    const directionGlyphColor =
      summary.direction === 'missed' ? theme.colors.accent.rose : theme.colors.text.secondary;

    return (
      <Pressable
        onPress={handlePress}
        onLongPress={handleLongPress}
        accessibilityRole="button"
        accessibilityState={selected ? { selected: true } : undefined}
        accessibilityLabel={`${callHistoryStrings.direction(summary.direction)} ${callHistoryStrings.kind(summary.kind)}, ${formattedTime}`}
        style={({ pressed }) => [
          styles.row,
          {
            backgroundColor: selected
              ? theme.colors.surface.secondary
              : theme.colors.surface.primary,
            borderBottomColor: theme.colors.border.secondary,
            opacity: pressed ? 0.85 : 1,
          },
        ]}
      >
        <View style={styles.avatarWrap}>
          <Avatar
            size="medium"
            {...(summary.avatarUri !== null ? { imageUri: summary.avatarUri } : {})}
            initials={summary.displayName}
            accessibilityLabel={summary.displayName}
          />
        </View>
        <View style={styles.body}>
          <View style={styles.topRow}>
            <Text
              style={[
                styles.title,
                {
                  color:
                    summary.direction === 'missed'
                      ? theme.colors.accent.rose
                      : theme.colors.text.primary,
                },
              ]}
              numberOfLines={1}
            >
              {summary.displayName}
            </Text>
            <Text style={[styles.timestamp, { color: theme.colors.text.tertiary }]}>
              {formattedTime}
            </Text>
          </View>
          <View style={styles.metaRow}>
            <Text
              style={[styles.glyph, { color: directionGlyphColor }]}
              accessibilityElementsHidden
            >
              {directionGlyph(summary.direction)}
            </Text>
            <Text
              style={[styles.glyph, { color: theme.colors.text.secondary }]}
              accessibilityElementsHidden
            >
              {kindGlyph(summary.kind)}
            </Text>
            <Text style={[styles.meta, { color: theme.colors.text.secondary }]} numberOfLines={1}>
              {summary.direction === 'missed'
                ? callHistoryStrings.direction(summary.direction)
                : callHistoryStrings.formatDuration(summary.durationSeconds)}
              {summary.otherParticipantCount > 0 ? ` · +${summary.otherParticipantCount}` : ''}
            </Text>
          </View>
        </View>
        {onToggleFavorite ? (
          <Pressable
            onPress={handleToggleFavorite}
            accessibilityRole="button"
            accessibilityLabel={
              summary.isFavorite
                ? callHistoryStrings.actionRemoveFromFavorites
                : callHistoryStrings.actionAddToFavorites
            }
            style={styles.starWrap}
            hitSlop={8}
          >
            <Text
              style={[
                styles.star,
                {
                  color: summary.isFavorite
                    ? theme.colors.accent.amber
                    : theme.colors.text.tertiary,
                },
              ]}
            >
              {summary.isFavorite ? '★' : '☆'}
            </Text>
          </Pressable>
        ) : null}
      </Pressable>
    );
  },
);

CallHistoryRow.displayName = 'CallHistoryRow';

function directionGlyph(direction: CallDirection): string {
  switch (direction) {
    case 'incoming':
      return '↙';
    case 'outgoing':
      return '↗';
    case 'missed':
      return '✗';
  }
}

function kindGlyph(kind: CallKind): string {
  switch (kind) {
    case 'audio':
      return '☎';
    case 'video':
      return '◎';
    case 'group':
      return '☷';
  }
}

const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;
const WEEK_MS = 7 * DAY_MS;

/**
 * Compact relative-time formatter. Mirrors the Activity-feed helper —
 * does not depend on `Intl.RelativeTimeFormat` (Hermes / macOS tooling
 * support is patchy). Exported via the index for tests.
 */
export function formatRelativeTime(timestamp: number, now: number = Date.now()): string {
  const delta = now - timestamp;
  if (delta < 0) {
    return 'now';
  }
  if (delta < HOUR_MS) {
    const m = Math.max(1, Math.floor(delta / 60_000));
    return `${m}m`;
  }
  if (delta < DAY_MS) {
    return `${Math.floor(delta / HOUR_MS)}h`;
  }
  if (delta < WEEK_MS) {
    return `${Math.floor(delta / DAY_MS)}d`;
  }
  return new Date(timestamp).toISOString().slice(0, 10);
}

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: StyleSheet.hairlineWidth,
  } satisfies ViewStyle,
  avatarWrap: {
    marginRight: 12,
  } satisfies ViewStyle,
  body: {
    flex: 1,
  } satisfies ViewStyle,
  topRow: {
    flexDirection: 'row',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    marginBottom: 2,
  } satisfies ViewStyle,
  title: {
    flex: 1,
    fontSize: 14,
    fontWeight: '600',
    marginRight: 8,
  } satisfies TextStyle,
  timestamp: {
    fontSize: 11,
    fontWeight: '500',
  } satisfies TextStyle,
  metaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  } satisfies ViewStyle,
  glyph: {
    fontSize: 13,
    fontWeight: '600',
  } satisfies TextStyle,
  meta: {
    fontSize: 12,
    fontWeight: '500',
  } satisfies TextStyle,
  starWrap: {
    marginLeft: 8,
    paddingHorizontal: 6,
    paddingVertical: 4,
  } satisfies ViewStyle,
  star: {
    fontSize: 18,
    fontWeight: '700',
  } satisfies TextStyle,
});
